/**
 * The tenant key the workspace's client-side cache is namespaced on.
 *
 * NO ENDPOINT IN THIS STEP ACCEPTS A TENANT IDENTIFIER (spec #44 §5) — the
 * server reads the tenant from the JWT, and this module reads the same
 * claim purely so the CLIENT can keep one tenant's cache, watermarks and
 * Ask transcripts out of another's (§8 gate 5). It is a cache key, never
 * an authorization input, and it is never sent anywhere.
 *
 * Deliberately not a hook: `useAuth()` needs the Frontegg provider mounted,
 * and the app runs without it in legacy mode, vitest and the API-stubbed
 * Playwright run. Reading the token the services already send works in all
 * four.
 */

import { getAuthToken } from './apiClient';

/** Used when no token or no tenant claim is available. Stable on purpose:
 * an unauthenticated session gets ONE namespace, not a new one per load. */
export const ANONYMOUS_TENANT = 'anonymous';

function decodeSegment(segment: string): unknown {
  // base64url → base64, then atob. Anything unexpected is a non-token.
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return JSON.parse(atob(padded));
}

/**
 * The tenant id from a JWT's payload, or `ANONYMOUS_TENANT`.
 *
 * Never throws: a malformed token must degrade to a private namespace, not
 * take down the shell that was about to render.
 *
 * @param token The JWT to read; defaults to the one the services send.
 */
export function resolveTenantKey(
  token: string | null = getAuthToken()
): string {
  if (!token) return ANONYMOUS_TENANT;
  const parts = token.split('.');
  if (parts.length < 2) return ANONYMOUS_TENANT;
  try {
    const payload = decodeSegment(parts[1]);
    if (typeof payload !== 'object' || payload === null) {
      return ANONYMOUS_TENANT;
    }
    const claims = payload as Record<string, unknown>;
    // THE ORDER MIRRORS THE SERVER, and must keep mirroring it
    // (backend/src/platform/tenant_context.py:564,593):
    //     org_id           = payload["org_id"] or payload["organizationId"]
    //     active_tenant_id = payload["active_tenant_id"] or org_id
    //
    // `active_tenant_id` FIRST is the load-bearing part. An operator token
    // carries a constant `org_id` (the agency) and a changing
    // `active_tenant_id` (the tenant being acted as), so keying on `org_id`
    // would hold one key across a tenant switch: `WorkspaceProviderInner`
    // would never remount, and tenant A's cached pages, watermarks and Ask
    // transcripts would stay on screen against tenant B's answers — exactly
    // the bleed §8 gate 5 exists to prevent.
    //
    // The three trailing claims are legacy dev/agency tokens in
    // localStorage that predate the org_id convention. They are last
    // because no server path reads them.
    const tenant =
      claims.active_tenant_id ??
      claims.org_id ??
      claims.organizationId ??
      claims.tenantId ??
      claims.tenant_id ??
      claims.tid;
    return typeof tenant === 'string' && tenant ? tenant : ANONYMOUS_TENANT;
  } catch {
    return ANONYMOUS_TENANT;
  }
}
